// GS Mandal — Infrastructure role dashboard.
// Shows Vendor + Tender KPIs and quick-actions; other modules are hidden by RBAC.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Building2, FileText, Plus, ArrowRight, Clock, CheckCircle2, XCircle, Archive, Users, Loader2, CalendarDays, Home } from "lucide-react";

const VENDOR_STATUSES = ["Pending", "Under Review", "Approved", "Rejected"];
const TENDER_STATUSES = ["Draft", "Published", "Closed", "Cancelled", "Archived"];

const fmt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };
const todayYMD = () => new Date().toISOString().slice(0, 10);
const addDaysYMD = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function InfraDashboard() {
  const [loading, setLoading] = useState(true);
  const [vendorCounts, setVendorCounts] = useState({});
  const [tenderCounts, setTenderCounts] = useState({});
  const [recentTenders, setRecentTenders] = useState([]);
  const [recentVendors, setRecentVendors] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Vendors — pull each status bucket
        const vendorRes = await Promise.all(VENDOR_STATUSES.map((s) => api.get("/admin/vendors", { params: { status: s } })));
        const vc = {}; VENDOR_STATUSES.forEach((s, i) => vc[s] = (vendorRes[i].data || []).length);
        setVendorCounts(vc);
        setRecentVendors((vendorRes[0].data || []).slice(0, 5));  // Pending

        // Tenders — pull each status bucket
        const tenderRes = await Promise.all(TENDER_STATUSES.map((s) => api.get("/admin/tenders", { params: { status: s } })));
        const tc = {}; TENDER_STATUSES.forEach((s, i) => tc[s] = (tenderRes[i].data || []).length);
        setTenderCounts(tc);
        setRecentTenders((tenderRes[1].data || []).slice(0, 5));  // Published

        // Upcoming amenity bookings — today .. today+7 days
        try {
          const bookings = await api.get("/admin/amenity-bookings", {
            params: { scope: "current", date_to: addDaysYMD(7) },
          });
          const items = (bookings.data?.items || [])
            .filter((b) => !["REJECTED", "CANCELLED", "COMPLETED"].includes((b.status || "").toUpperCase()))
            .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
          setUpcomingBookings(items);
        } catch { /* ignore */ }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const today = useMemo(() => todayYMD(), []);

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto space-y-8" data-testid="infra-dashboard">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Infrastructure · Procurement Overview</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Welcome back</h1>
        <p className="mt-2 text-sm text-[#6b655b]">Quick view of vendor empanelment and tenders across GS Mandal.</p>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading dashboard…</div>
      ) : (
        <>
          {/* Vendor KPIs */}
          <section data-testid="infra-vendor-kpis">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-bold flex items-center gap-2"><Building2 size={13} /> Vendor Empanelment</div>
              <Link to="/admin/vendors" className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] inline-flex items-center gap-1 font-bold">Open module <ArrowRight size={11} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard testid="kpi-vendor-pending" icon={<Clock size={14} />} label="Pending" value={vendorCounts["Pending"] || 0} to="/admin/vendors" accent="#c78e42" />
              <StatCard testid="kpi-vendor-review" icon={<Users size={14} />} label="Under Review" value={vendorCounts["Under Review"] || 0} to="/admin/vendors" accent="#0D5C63" />
              <StatCard testid="kpi-vendor-approved" icon={<CheckCircle2 size={14} />} label="Approved" value={vendorCounts["Approved"] || 0} to="/admin/vendors" accent="#0f7a45" />
              <StatCard testid="kpi-vendor-rejected" icon={<XCircle size={14} />} label="Rejected" value={vendorCounts["Rejected"] || 0} to="/admin/vendors" accent="#b02020" />
            </div>
          </section>

          {/* Tender KPIs */}
          <section data-testid="infra-tender-kpis">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-bold flex items-center gap-2"><FileText size={13} /> Tender Management</div>
              <div className="flex items-center gap-2">
                <Link to="/admin/tenders" className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] inline-flex items-center gap-1 font-bold">Open module <ArrowRight size={11} /></Link>
                <Link to="/admin/tenders" className="bg-[#234333] hover:bg-[#1a3125] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1" data-testid="infra-create-tender"><Plus size={11} /> Create Tender</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard testid="kpi-tender-draft" icon={<FileText size={14} />} label="Draft" value={tenderCounts["Draft"] || 0} to="/admin/tenders" accent="#c78e42" />
              <StatCard testid="kpi-tender-published" icon={<CheckCircle2 size={14} />} label="Published" value={tenderCounts["Published"] || 0} to="/admin/tenders" accent="#0f7a45" />
              <StatCard testid="kpi-tender-closed" icon={<XCircle size={14} />} label="Closed" value={tenderCounts["Closed"] || 0} to="/admin/tenders" accent="#0D5C63" />
              <StatCard testid="kpi-tender-cancelled" icon={<XCircle size={14} />} label="Cancelled" value={tenderCounts["Cancelled"] || 0} to="/admin/tenders" accent="#b02020" />
              <StatCard testid="kpi-tender-archived" icon={<Archive size={14} />} label="Archived" value={tenderCounts["Archived"] || 0} to="/admin/tenders" accent="#5B5B5B" />
            </div>
          </section>

          {/* Upcoming Amenity Bookings — Next 7 Days */}
          <section data-testid="infra-upcoming-bookings">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-bold flex items-center gap-2"><CalendarDays size={13} /> Upcoming Amenity Bookings · Next 7 Days</div>
              <Link to="/admin/amenity-bookings" className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] inline-flex items-center gap-1 font-bold">Open module <ArrowRight size={11} /></Link>
            </div>
            <UpcomingBookingsPanel items={upcomingBookings} today={today} />
          </section>

          {/* Two-column recent lists */}
          <section className="grid md:grid-cols-2 gap-5">
            <RecentList
              testid="infra-recent-pending-vendors"
              title="Pending vendor reviews"
              empty="No pending vendors."
              cta={{ to: "/admin/vendors", label: "Review all" }}
              items={recentVendors.map((v) => ({
                id: v.id, primary: v.applicant_firm_name, secondary: `${v.company_type} · ${(v.company_categories || []).join(', ') || '—'}`, tertiary: fmt(v.created_at),
              }))}
            />
            <RecentList
              testid="infra-recent-published-tenders"
              title="Recent published tenders"
              empty="No published tenders yet."
              cta={{ to: "/admin/tenders", label: "Manage all" }}
              items={recentTenders.map((t) => ({
                id: t.id, primary: t.title, secondary: `${t.reference_number || 'No ref'} · ${t.tender_type || ''}`, tertiary: `Last date: ${t.last_submission_datetime || '—'}`,
              }))}
            />
          </section>
        </>
      )}
    </div>
  );
}

function UpcomingBookingsPanel({ items, today }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white border border-[#e5e0d8] rounded-sm p-6 text-center text-sm text-[#6b655b]" data-testid="infra-no-bookings">
        No amenity bookings scheduled in the next 7 days.
      </div>
    );
  }
  const statusPill = (s) => {
    const st = (s || "").toUpperCase();
    if (st === "CONFIRMED") return "bg-[#eef3ea] text-[#234333] border-[#234333]/30";
    if (st === "PROVISIONAL" || st === "UNDER_REVIEW") return "bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/50";
    return "bg-gray-100 text-gray-700 border-gray-300";
  };
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden" data-testid="infra-bookings-table">
      <div className="grid grid-cols-[100px_1fr_120px_140px_100px] gap-3 px-4 py-2.5 bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b] font-semibold">
        <div>Date</div>
        <div>Amenity · Applicant</div>
        <div>Duration</div>
        <div>Ref</div>
        <div className="text-right">Status</div>
      </div>
      <div className="divide-y divide-[#f0ece2]">
        {items.map((b) => {
          const isStay = !!b.is_stay || !!b.check_in_date;
          const bookingDate = (isStay ? b.check_in_date : b.event_date) || "";
          const isToday = bookingDate === today;
          const durLabel = isStay
            ? `${b.nights || 0} night${b.nights === 1 ? "" : "s"}`
            : (b.duration || "—");
          return (
            <div
              key={b.booking_ref}
              data-testid={`infra-booking-${b.booking_ref}`}
              className={`grid grid-cols-[100px_1fr_120px_140px_100px] gap-3 px-4 py-3 text-sm items-center ${isToday ? "bg-gradient-to-r from-[#FEF7E4] to-transparent border-l-4 border-l-[#F26419]" : ""}`}
            >
              <div>
                <div className={`font-semibold ${isToday ? "text-[#F26419]" : "text-[#1c1a17]"}`}>
                  {new Date(bookingDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </div>
                {isToday && <div className="text-[9px] tracking-widest uppercase text-[#F26419] font-bold">Today</div>}
                {isStay && b.check_out_date && (
                  <div className="text-[10px] text-[#8a8478]">→ {new Date(b.check_out_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[#1c1a17] truncate">{b.amenity_title || "—"}</div>
                <div className="text-[11px] text-[#8a8478] truncate">{b.applicant_name || "—"}{b.event_name ? ` · ${b.event_name}` : ""}</div>
              </div>
              <div className="text-[11px] text-[#5b5650]">{durLabel}</div>
              <div className="text-[11px] text-[#5b5650] font-mono truncate">{b.booking_ref}</div>
              <div className="text-right">
                <span className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-sm border ${statusPill(b.status)}`}>{b.status || "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ testid, icon, label, value, to, accent }) {
  return (
    <Link to={to} data-testid={testid} className="bg-white border border-[#e5e0d8] rounded-sm p-4 hover:shadow-md hover:border-[#234333]/40 transition-all group block">
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>{icon}</div>
        <ArrowRight size={13} className="text-[#8a8478] group-hover:text-[#234333] transition-colors" />
      </div>
      <div className="mt-3 font-serif text-3xl text-[#1c1a17]">{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mt-0.5">{label}</div>
    </Link>
  );
}

function RecentList({ testid, title, empty, cta, items }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#c78e42] font-bold">{title}</div>
        <Link to={cta.to} className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] font-bold inline-flex items-center gap-1">{cta.label} <ArrowRight size={11} /></Link>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#8a8478] py-3">{empty}</div>
      ) : (
        <ul className="divide-y divide-[#f0ebe0]">
          {items.map((it) => (
            <li key={it.id} className="py-2.5">
              <div className="text-sm font-semibold text-[#1c1a17]">{it.primary}</div>
              <div className="text-[11px] text-[#6b655b] mt-0.5">{it.secondary}</div>
              <div className="text-[10px] text-[#8a8478] mt-0.5">{it.tertiary}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
